import {
  conversationListResponseSchema,
  conversationMutationResponseSchema,
  conversationMessagesResponseSchema,
  conversationClaimSearchResponseSchema,
  messageAttachmentDownloadResponseSchema,
  prepareMessageAttachmentsResponseSchema,
  type ConversationListResponse,
  type ConversationMessagesResponse,
  type ConversationClaimSearchResponse,
  type MessageAttachmentUploadTarget,
} from '@billing/validation';
import {
  getRequest as get,
  postRequest as post,
  putRequest as put,
} from 'api/utils/HttpMethods';
import queryString from 'api/utils/queryStringBuilder';

export const getConversations = async (
  cursor?: string
): Promise<ConversationListResponse> => {
  const result = await get<ConversationListResponse>(
    'QBilling',
    `/resource/conversation/user${queryString({ cursor, pageSize: 50 })}`
  );
  return conversationListResponseSchema.parse(
    result ?? { data: [], nextCursor: null }
  );
};

export const getConversationMessages = async (
  conversationID: string,
  cursor?: string
): Promise<ConversationMessagesResponse> => {
  const result = await get<ConversationMessagesResponse>(
    'QBilling',
    `/resource/conversation/messages${queryString({
      conversationID,
      cursor,
      pageSize: 50,
    })}`
  );
  return conversationMessagesResponseSchema.parse(
    result ?? { data: [], nextCursor: null, isActiveMember: false }
  );
};

export const searchConversationClaims = async (input: {
  conversationID: string;
  query?: string;
  claimID?: string;
  cursor?: string;
  pageSize?: number;
}): Promise<ConversationClaimSearchResponse> => {
  const result = await get<ConversationClaimSearchResponse>(
    'QBilling',
    `/resource/conversation/claims${queryString({
      ...input,
      query: input.query ? encodeURIComponent(input.query) : undefined,
    })}`
  );
  return conversationClaimSearchResponseSchema.parse(
    result ?? { data: [], nextCursor: null }
  );
};

export const createConversation = async (input: {
  kind: 'direct' | 'group';
  title?: string;
  participantIDs: string[];
}): Promise<string> => {
  const result = await post<{ conversationID: string }, typeof input>(
    'QBilling',
    '/resource/conversation',
    input
  );
  return conversationMutationResponseSchema.parse(result).conversationID;
};

export const updateConversation = async (input: {
  conversationID: string;
  title: string;
  participantIDs: string[];
}): Promise<void> => {
  await put('QBilling', '/resource/conversation', input);
};

export const markConversationRead = async (
  conversationID: string,
  messageID: string
): Promise<void> => {
  await put('QBilling', '/resource/conversation/read', {
    conversationID,
    messageID,
  });
};

export const prepareMessageAttachments = async (
  conversationID: string,
  files: File[]
): Promise<MessageAttachmentUploadTarget[]> => {
  const result = await post<MessageAttachmentUploadTarget[], object>(
    'QBilling',
    '/resource/conversation/attachments',
    {
      conversationID,
      files: files.map((file) => ({
        originalFilename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      })),
    }
  );
  return prepareMessageAttachmentsResponseSchema.parse(result ?? []);
};

export const getMessageAttachmentDownloadURL = async (
  attachmentID: string
): Promise<string> => {
  const result = await get<{ downloadURL: string }>(
    'QBilling',
    `/resource/conversation/attachments/download${queryString({
      attachmentID,
    })}`
  );
  return messageAttachmentDownloadResponseSchema.parse(result).downloadURL;
};
